import React from 'react';
import { reportError } from '../services/errorReporter';

/**
 * LocalErrorBoundary — scoped error boundary that isolates a single
 * subtree so its failure degrades gracefully instead of taking down the
 * whole page via the top-level <ErrorBoundary>.
 *
 * Motivation (#1054): ParcelDetail renders the WebGL <Canvas> terrain
 * viewer (ParcelTerrain3D). When WebGL context creation fails — a
 * headless/no-GPU browser, or a real browser that has exhausted its
 * ~16 live-context limit after navigating several parcels — react-three-
 * fiber throws synchronously during render. With no local boundary that
 * error bubbled to App.js's top-level ErrorBoundary and replaced the
 * ENTIRE parcel page (including the perfectly-valid listing photo
 * gallery) with "Something went wrong". The reported symptom was a
 * gallery showing "No image" for a parcel that in fact had 25 photos —
 * the gallery never got a chance to render because the page had already
 * crashed on the terrain canvas.
 *
 * Wrapping just the terrain in this boundary lets a WebGL failure fall
 * back to the `fallback` element (the hillshade preview) while every
 * other section — gallery included — renders normally.
 *
 * Reset semantics: the boundary clears its error state whenever the
 * `resetKey` prop changes (e.g. the parcel id), so navigating to a new
 * parcel re-attempts the render instead of staying stuck on the
 * fallback.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props, state) {
    // Clear the error when the caller signals a fresh context (new
    // parcel). Without this a boundary that errored once would keep
    // showing the fallback for every subsequent parcel.
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.warn(
      `LocalErrorBoundary (${this.props.label || 'unlabeled'}) caught:`,
      error,
    );
    // Report at low severity — this is a contained, recoverable
    // degradation, not a page-killing crash. Tag the source so the
    // admin client-errors view can distinguish it from top-level
    // ErrorBoundary hits.
    reportError({
      message: error?.message || String(error),
      stack: error?.stack,
      source: `LocalErrorBoundary:${this.props.label || 'unlabeled'}`,
      componentStack: errorInfo?.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default LocalErrorBoundary;
